<template>
  <loading v-if="isLoading" class="flex justify-center mt-5 py-10" />
  <alt-table
    v-else-if="patientsClinician.length > 0"
    :columns="COLUMNS"
    pagination
    :paginationOptions="paginationOptions"
    @onPagination="$emit('onPagination', $event)"
  >
    <template #t-row>
      <tr v-for="(s, index) in getPageItems" :key="s.id">
        <td>{{ index + 1 }}</td>
        <td class="text-center">{{ s.patient.name }}</td>
        <td class="text-center">{{ s.patient.id }}</td>
        <td class="actions">
          <button class="btn-action btn btn-primary" @click="openModal(s.patient.id)">👁️</button>
        </td>
      </tr>
    </template>
  </alt-table>
  <div v-else class="flex flex-column justify-center gap-2 mt-5 py-10 align-center">
    <p class="text-center">No patients found for this clinician study.</p>
    <router-link
      :to="{
        name: ROUTES.HOME,
      }"
      class="flex justify-center w-15 btn btn-primary"
      >Back to studies</router-link
    >
  </div>

  <patient-clinician-exercise-modal
    :visible="showModal"
    :patientName="selectedPatientName"
    :patientId="selectedPatientId"
    :exercises="selectedExercises"
    :loadingId="isLoadingDownloadId"
    @close="showModal = false"
    @download="downloadExercise"
  />
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { toast } from 'vue3-toastify';
import { useOptions } from '@/components/common/paginationOptions';
import { getPatientsByClinicianStudyId } from '@/studies/services/studies';
import altTable from '@/components/tables/altTable.vue';
import PatientClinicianExerciseModal from '@/patients/atoms/PatientClinicianExerciseModal.vue';
import { downloadFolderContents } from '../services/patients';
import Loading from '@/components/loading.vue';
import { ROUTES } from '@/router';

const route = useRoute();
const patientsClinician = ref([]);
const isLoading = ref(true);
const COLUMNS = [
  { header: 'N°' },
  { header: 'Name' },
  { header: 'Identifier' },
  { header: 'Actions' },
];
const currentPage = ref(1);
const limit = ref(10);
const patientsUniqueNotDuplicated = ref([]);
const { paginationOptions, handlePagination, getPageItems } = useOptions(patientsClinician);
const isLoadingDownloadId = ref(false);

const showModal = ref(false);
const selectedPatientId = ref(null);

const selectedExercises = computed(() => {
  return patientsClinician.value.filter((item) => item.patient.id === selectedPatientId.value);
});

const selectedPatientName = computed(() => {
  const first = patientsClinician.value.find((item) => item.patient.id === selectedPatientId.value);
  return first ? first.patient.name : '';
});

onMounted(async () => {
  try {
    isLoading.value = true;
    patientsClinician.value = await getPatientsByClinicianStudyId(route.params.clinicianStudyId);
    patientsUniqueNotDuplicated.value = patientsClinician.value.filter(
      (patient, index, self) => index === self.findIndex((t) => t.patient.id === patient.patient.id)
    );
  } catch (err) {
    toast.error(err.message);
  } finally {
    isLoading.value = false;
  }
});

watch(currentPage, (newPage) => {
  handlePagination(newPage, limit.value);
});

function openModal(patientId) {
  selectedPatientId.value = patientId;
  showModal.value = true;
}

async function downloadExercise(ex) {
  try {
    isLoadingDownloadId.value = ex.videoReferencePath;
    const videoPath = ex.videoReferencePath;
    const date = ex.date;
    const name = ex.sessionType;
    const patientId = ex.patient.id;
    await downloadFolderContents(videoPath, date, name, patientId);
  } catch (error) {
    toast.error(`Sorry, something went wrong: ${error.message}. Please try again later.`);
  } finally {
    isLoadingDownloadId.value = null;
  }
}
</script>

<style scoped lang="scss">
.btn {
  padding: 0.25rem 0.5rem;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
}

.btn-secondary {
  background-color: #6c757d;
  color: #fff;
}

.btn-sm {
  font-size: 0.875rem;
}

.modal-hide {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  font-size: 18px;
  color: #333;
  cursor: pointer;
}

.gap-2 {
  gap: 0.5rem;
}

.display-none {
  display: none;
}

.display-initial {
  display: initial;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
